import { Container, Title, Accordion, Text, ThemeIcon, rem, Grid } from '@mantine/core';
import { motion } from 'framer-motion';
import { HOME_CONTENT } from '../../data/homeContent';

const MotionGridCol = motion.create(Grid.Col);

/**
 * FAQ component displays a list of frequently asked questions using an accordion.
 * It can accept custom data or default to the home page FAQ.
 * @param {Object} props - Component properties.
 * @param {Array} props.customData - Optional array of FAQ items to display.
 */
export default function FAQ({ customData }) {
  const { faq } = HOME_CONTENT;
  const data = customData || faq.items;

  return (
    <Container size="lg" py={{ base: 80, md: 120 }} id="faq" style={{ position: 'relative', overflow: 'hidden' }}>
       <div style={{
          position: 'absolute', bottom: '-50px', right: '-50px', width: '500px', height: '500px',
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='400' height='400' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg stroke='%23ffffff' stroke-width='0.5' fill='none' stroke-opacity='0.05'%3E%3Cpath d='M20 20 L80 20 L80 80 L20 80 Z' /%3E%3Cpath d='M30 30 L70 30 L70 70 L30 70 Z' /%3E%3Cpath d='M20 20 L30 30 M80 20 L70 30 M80 80 L70 70 M20 80 L30 70' /%3E%3C/g%3E%3C/svg%3E")`,
          backgroundRepeat: 'no-repeat', opacity: 1, pointerEvents: 'none', zIndex: 0, transform: 'rotate(-10deg)'
      }} />

      <Grid gutter={60} style={{ position: 'relative', zIndex: 1 }}>
        <MotionGridCol
            span={{ base: 12, md: 5 }}
            initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
        >
            <Title order={2} fw={900} style={{ fontSize: '2.5rem', letterSpacing: '-1px', lineHeight: 1.1 }} mb="md">
                {faq.title} <span style={{ color: '#228be6' }}>{faq.titleHighlight}</span>
            </Title>
            <Text c="dimmed" size="lg" mb="xl">
                {faq.subtitle}
            </Text>
        </MotionGridCol>

        <MotionGridCol
            span={{ base: 12, md: 7 }}
            initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}
        >
            <Accordion
                variant="separated" radius="lg" defaultValue={data[0].id}
                styles={{
                    item: {
                        backgroundColor: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.05)',
                        transition: 'transform 0.2s ease, border-color 0.2s ease',
                        '&[data-active]': { backgroundColor: 'rgba(255, 255, 255, 0.06)', borderColor: 'rgba(34, 139, 230, 0.3)', transform: 'scale(1.01)' }
                    },
                    control: { color: 'white', padding: '1rem 1.5rem', '&:hover': { backgroundColor: 'transparent' } },
                    content: { padding: '0 1.5rem 1.5rem 1.5rem', color: 'var(--mantine-color-dimmed)', lineHeight: 1.6 },
                    chevron: { color: 'var(--mantine-color-dimmed)' }
                }}
            >
                {data.map((item) => (
                    <Accordion.Item key={item.id} value={item.id} mb="md">
                        <Accordion.Control icon={
                            <ThemeIcon color={item.color || 'gray'} variant="light" size="md" radius="md">
                                <item.icon style={{ width: rem(16), height: rem(16) }} />
                            </ThemeIcon>
                        }>
                            <Text fw={600} size="md">{item.question}</Text>
                        </Accordion.Control>
                        <Accordion.Panel>{item.answer}</Accordion.Panel>
                    </Accordion.Item>
                ))}
            </Accordion>
        </MotionGridCol>
      </Grid>
    </Container>
  );
}